import { EndOfTreatmentForm as EndOfTreatmentFormDto, LookupEotReasonType, LookupNumberDoseReductionsType, LookupYesNoUnknownType, Permission } from "@/api/dtos";
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import { Autocomplete, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, Numeric, PageLayout, Select, ToggleButtonGroup, Text, CollaboratingGroupContext, InstitutionContext, MasterGroupContext, PatientContext, RouteLoading, ICrfConditionParameters } from '@ngt/opms';
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext } from "react";

interface IEndOfTreatmentFormProps {

}

const EndOfTreatmentForm: FC<IEndOfTreatmentFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    // const eotReasonPatientDecision = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
    //     params.formState?.value?.eotReason === LookupEotReasonType.PatientDecision,
    // []);

    const eotReasonToxicity = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.Toxicity,
    []);

    const eotReasonDeath = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.Death,
    []);

    const eotReasonProgressiveDisease = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.ProgressiveDisease,
    []);

    const eotReasonOther = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.Other,
    []);

    const doseReduction = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.doseReduction === LookupYesNoUnknownType.Yes,
    []);

    const numberDoseReductionsOther = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.numberDoseReductions === LookupNumberDoseReductionsType.Other,
    []);

    const numberDoseReductionsAtLeastOne = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.numberDoseReductions === LookupNumberDoseReductionsType.One,
    []);

    const numberDoseReductionsAtLeastTwo = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.numberDoseReductions === LookupNumberDoseReductionsType.Two,
    []);

    const numberDoseReductionsAtLeastThree = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.numberDoseReductions === LookupNumberDoseReductionsType.Three,
    []);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <GroupedField
                    propertyPath="lastCycleDate"
                    format={"dd-MMM-yyyy"}
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField 
                    propertyPath="totalCyclesReceived"
                    component={Numeric}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="eotReason"
                    component={Autocomplete}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <CrfCondition
                    condition={eotReasonOther}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="eotOtherSpecify"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonToxicity}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="doseReduction"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={doseReduction}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numberDoseReductions"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsOther}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numberDoseReductionsOtherSpecify"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastOne}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeFirstReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastTwo}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeSecondReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastThree}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeThirdReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsOther}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="doseReductionNumCyclesOther"
                        component={Text}
                        multiline
                        rows={4}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonToxicity}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="toxicityAndGrade"
                        component={Text}
                        multiline
                        rows={4}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />                    
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonDeath}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfDeath"
                        component={DatePicker}
                        format={"dd-MMM-yyyy"}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonProgressiveDisease}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfProgression"
                        component={DatePicker}
                        format={"dd-MMM-yyyy"}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                {/*<CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfConsentWithdrawal"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="consentedToContinuedSampleUse"
                        component={RadioGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="consentedToFutureUnspecifiedSampleUse"
                        component={RadioGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition> */}
                <GroupedField
                    propertyPath="response"
                    component={Autocomplete}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="lastDateOfDiseaseAssessments"
                    component={DatePicker}
                    format="dd-MMM-yyyy"
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="additionalInformation"
                    component={Text}
                    multiline
                    rows={4}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
            </CrfForm>
        </PageLayout>
    );
}

export default EndOfTreatmentForm;